import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Edit, UserPlus, Share2, ArrowLeft, MapPin, Calendar, Clock, Mail, Loader2, ShieldCheck, AlertTriangle, Route } from '@/ui/icons'
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom'
import { useTrips, useTrip } from '@/modules/trips'
import { useTripData } from '@/modules/trips/hooks/useTripData'
import { useQueryClient } from '@tanstack/react-query'
import { useGuestTrip } from '@/modules/trips'
import { useRealtimeTripMembers } from '@/modules/trips'
import { useAuth, useGuestSession } from '@/modules/auth'
import { canEditTrip } from '@/shared/utils/auth'
import {
  Container,
  PageHeader,
  Button,
  Card,
  CardContent,
  Badge
} from '@/ui/design-system'
import { PageLayout } from '@/ui/layout'
import { TripEditModal, TripSettingsDropdown, GuestAccessDenied, GuestTripHeader, GuestTripContent, GuestTripCTA, ShareModal, FindFriendsModal, InviteDropdown, AddTagAlongModal } from '@/modules/trips/components'
import { TripOverviewTab } from '@/modules/trips/components/tabs/TripOverviewTab'
import { CrewSummarySection } from './components/CrewSummarySection'
import { GearSummarySection } from './components/GearSummarySection'
import { MealPlanSummarySection } from './components/MealPlanSummarySection'
import { SectionCard } from './components/SectionCard'
import { TripReadinessSection, type ChecklistItemId } from './components/TripReadinessSection'
import { TripNextSteps } from './components/TripNextSteps'
import { AnnouncementsSection } from './components/AnnouncementsSection'
import { TripCoverDialog } from './components/TripCoverDialog'
import { TripGallerySection } from './components/TripGallerySection'
import { TripsApi } from '@/shared/api'
import { formatTripDateRangeWithWeekdays, getTripDurationDays } from '@/modules/trips/utils/dateHelpers'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { useTripDetailPanel } from './contexts/TripDetailPanelContext'
import { useTripOverviewData } from '@/modules/trips/hooks/useTripOverviewData'
import { TripItineraryView, TripRoute3DPreview } from '@/modules/trips/components/views'
import { MapboxMap } from '@/modules/routes/components'
import { cn } from '@/shared/utils'
import type { TripResponse } from '@/shared/api/types'
import { ApiError } from '@/shared/api/types'
import type { TripRouteScheduleRoute } from '@/shared/api/routes'
import { PageMeta } from '@/components/PageMeta'

function getTripStatus(trip: TripResponse): 'active' | 'planning' | 'completed' {
  const now = new Date()
  const startDate = trip.start_date ? new Date(trip.start_date) : null
  const endDate = trip.end_date ? new Date(trip.end_date) : null
  const completedAt = trip.completed_at ? new Date(trip.completed_at) : null

  if (startDate && endDate && now >= startDate && now <= endDate) return 'active'
  if (completedAt || (endDate && now > endDate)) return 'completed'
  return 'planning'
}

function getTripStatusLabel(status: 'active' | 'planning' | 'completed'): string {
  switch (status) {
    case 'active': return 'Active'
    case 'planning': return 'Planning'
    case 'completed': return 'Completed'
  }
}

function getMemberDisplayName(member: { guest_name?: string | null; user?: { user_metadata?: { full_name?: string }; email?: string } }): string {
  if (member.guest_name) return member.guest_name
  const name = member.user?.user_metadata?.full_name ?? member.user?.email
  return name ?? 'Crew member'
}

/**
 * Content-only trip detail for authenticated users. Renders inside TripsLayout (no PageLayout).
 * Used for /trips/:tripId when inside the Teams-style layout.
 */
export function TripDetailContent() {
  const { tripId } = useParams()
  const { user, getAccessToken } = useAuth()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isFindFriendsModalOpen, setIsFindFriendsModalOpen] = useState(false)
  const [isCoverDialogOpen, setIsCoverDialogOpen] = useState(false)
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false)
  const [addTagAlongParent, setAddTagAlongParent] = useState<{ id: string; name: string } | null>(null)
  const queryClient = useQueryClient()
  const { loadTrip } = useTrip(tripId || '')

  const handleTripMemberUpdate = useCallback(async () => {
    await loadTrip()
    queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
  }, [loadTrip, queryClient, tripId])

  // Use new unified hook with React Query for better performance
  const { trip, subscription, meals, gear, waypoints, members, isLoading, error, refetch } = useTripData(tripId || '', {
    includeSubscription: true
  })
  const { deleteTrip, updateTrip, loadTrips, archiveTrip, unarchiveTrip, restoreTripFromRetention } = useTrips()
  const [isRestoringRetention, setIsRestoringRetention] = useState(false)

  const isAdminView = !!(trip && (trip as { is_admin_view?: boolean }).is_admin_view)
  const canEdit = !isAdminView && !!user && !!trip && canEditTrip(trip, user)
  const isPrimaryMember =
    !!user &&
    !!trip?.members &&
    trip.members.some((m) => !m.parent_member_id && m.user_id === user.id)

  // Subscription features from unified hook
  const canUseRanger = subscription?.features.canUseRanger ?? false

  const memoizedRefetch = useCallback(() => refetch(), [refetch])
  useRealtimeTripMembers({
    tripId: tripId || '',
    onMemberUpdate: memoizedRefetch,
    enabled: !!tripId && !!user
  })

  const handleDeleteFromSettings = useCallback(async () => {
    if (!trip) return
    await deleteTrip(trip.id)
  }, [trip, deleteTrip])

  const handleMemberUpdate = async () => {
    try {
      refetch()
    } catch {}
  }

  const handleTripUpdate = async () => {
    try {
      refetch()
    } catch (e) {
    }
  }

  const handleTripEdit = async (tripData: {
    name: string
    description?: string
    start_date?: string
    end_date?: string
    crew_limit?: number | null
    radio_channel?: string
  }) => {
    if (!trip) return
    setIsSaving(true)
    try {
      await updateTrip(trip.id, tripData)
      setIsEditModalOpen(false)
      refetch()
      // Setting dates also moves undated meals onto days.
      if (tripData.start_date) queryClient.invalidateQueries({ queryKey: ['meals', trip.id] })
    } catch (e) {
      alert('Failed to update trip. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const syncTripAfterArchiveChange = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
    await refetch()
  }, [queryClient, tripId, refetch])

  const handleArchiveTrip = useCallback(async () => {
    if (!tripId || !trip) return
    await archiveTrip(trip.id)
    await syncTripAfterArchiveChange()
  }, [archiveTrip, trip, tripId, syncTripAfterArchiveChange])

  const handleUnarchiveTrip = useCallback(async () => {
    if (!tripId || !trip) return
    await unarchiveTrip(trip.id)
    await syncTripAfterArchiveChange()
  }, [unarchiveTrip, trip, tripId, syncTripAfterArchiveChange])

  const handleRestoreRetention = useCallback(async () => {
    if (!tripId || !trip) return
    setIsRestoringRetention(true)
    try {
      await restoreTripFromRetention(trip.id)
      queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
      await refetch()
      await loadTrips(undefined, true)
    } catch (e) {
      alert('Could not extend trip retention. Please try again.')
    } finally {
      setIsRestoringRetention(false)
    }
  }, [tripId, trip, restoreTripFromRetention, queryClient, refetch, loadTrips])

  const retentionPurgeDeadline =
    trip?.retention_window_started_at != null
      ? new Date(new Date(trip.retention_window_started_at).getTime() + 30 * 24 * 60 * 60 * 1000)
      : null

  const getDaysUntilDeparture = () => {
    if (!trip?.start_date) return null
    const [y, m, d] = trip.start_date.split('-').map(Number)
    const start = new Date(y, m - 1, d)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return Math.ceil((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  }
  const daysUntil = getDaysUntilDeparture()
  const dateRange = formatTripDateRangeWithWeekdays(trip?.start_date ?? null, trip?.end_date ?? null)
  const duration = trip ? getTripDurationDays(trip) : null

  const { setSelectedDetail, selectedDetail, onWaypointDeletedRef } = useTripDetailPanel()

  const handleChecklistItemClick = useCallback(
    (itemId: ChecklistItemId) => {
      switch (itemId) {
        case 'radio':
        case 'details':
          setIsEditModalOpen(true)
          break
        case 'itinerary':
          document.getElementById('route-itinerary-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          break
        case 'meals':
          document.getElementById('meal-plan-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          requestAnimationFrame(() => {
            setSelectedDetail({ type: 'meal-add' })
          })
          break
        case 'gear':
          document.getElementById('gear-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          requestAnimationFrame(() => {
            setSelectedDetail({ type: 'gear-add' })
          })
          break
        case 'crew':
          document.getElementById('crew-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          setIsShareModalOpen(true)
          break
      }
    },
    [setSelectedDetail]
  )

  const { waypoints: overviewWaypoints, routeSchedule, isLoading: isOverviewLoading, refresh, updateWaypoint, deleteWaypoint, reorderWaypoints } = useTripOverviewData(tripId || '')

  // When the right panel mutates a waypoint (edit or delete), only refresh itinerary data
  // so only the Route & Itinerary section updates—no full trip refetch or whole-page loading.
  useEffect(() => {
    if (!onWaypointDeletedRef) return
    onWaypointDeletedRef.current = async () => {
      await refresh({ cache: 'no-store' })
    }
    return () => {
      onWaypointDeletedRef.current = null
    }
  }, [onWaypointDeletedRef, refresh])

  const scheduledRoutes: TripRouteScheduleRoute[] = useMemo(
    () => (routeSchedule ? routeSchedule.days.flatMap((d) => d.routes) : []),
    [routeSchedule]
  )

  const sortedWaypoints = useMemo(
    () =>
      [...(overviewWaypoints ?? [])].sort((a, b) => {
        const dayA = a.trip_day ?? 1
        const dayB = b.trip_day ?? 1
        if (dayA !== dayB) return dayA - dayB
        return (a.order_index ?? 0) - (b.order_index ?? 0)
      }),
    [overviewWaypoints]
  )

  const selectedWaypointId = selectedDetail?.type === 'waypoint' ? selectedDetail.id : null

  // Routes array for app MapboxMap (same format as itinerary-builder / backend)
  const tripDetailRoutes = useMemo(
    () => scheduledRoutes.map((sr) => sr.route).filter((r): r is NonNullable<typeof r> => Boolean(r?.geometry)),
    [scheduledRoutes]
  )

  // Waypoints with trip_id so itinerary numbered markers show
  const waypointsWithTripId = useMemo(
    () =>
      (tripId ? sortedWaypoints.map((wp) => ({ ...wp, trip_id: wp.trip_id ?? tripId })) : sortedWaypoints) as typeof sortedWaypoints,
    [sortedWaypoints, tripId]
  )

  const selectedWaypoint = useMemo(
    () => (selectedWaypointId ? sortedWaypoints.find((w) => w.id === selectedWaypointId) ?? null : null),
    [sortedWaypoints, selectedWaypointId]
  )

  const centerOnWaypoints = useMemo(
    () =>
      sortedWaypoints
        .filter((wp) => wp.latitude != null && wp.longitude != null)
        .map((wp) => ({ latitude: wp.latitude!, longitude: wp.longitude! })),
    [sortedWaypoints]
  )

  const location = useLocation()
  useEffect(() => {
    if (isLoading || !trip) return
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    const sectionId =
      tab === 'crew' ? 'crew-section' : tab === 'gear' ? 'gear-section' : tab === 'meals' ? 'meal-plan-section' : null
    if (sectionId) {
      const timer = setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [location.search, isLoading, trip])

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 space-y-6 animate-pulse">
        <div className="space-y-2">
          <div className="h-8 w-64 max-w-md rounded bg-earth-200" />
          <div className="h-4 w-full max-w-xl rounded bg-earth-100" />
          <div className="h-4 w-48 rounded bg-earth-100" />
        </div>
        <div className="flex gap-2 border-b border-earth-200 pb-2">
          <div className="h-10 w-24 rounded bg-earth-200" />
          <div className="h-10 w-24 rounded bg-earth-100" />
          <div className="h-10 w-24 rounded bg-earth-100" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="h-48 rounded-lg bg-earth-100" />
          <div className="h-48 rounded-lg bg-earth-100" />
          <div className="h-48 rounded-lg bg-earth-100" />
        </div>
      </div>
    )
  }

  if (error) {
    const isTripDeleted = error instanceof ApiError && error.status === 404
    return (
      <div className="p-8">
        <PageHeader
          title={isTripDeleted ? "Trip No Longer Available" : "Error Loading Trip"}
          subtitle={isTripDeleted ? "This trip has been removed by the owner" : "There was a problem loading the trip details"}
          actions={
            <Link to="/trips">
              <Button variant="outline" className="flex items-center space-x-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Trips</span>
              </Button>
            </Link>
          }
        />
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              {isTripDeleted ? (
                <p className="text-muted-foreground mb-4">
                  This trip is no longer available. The owner has deleted it and it can no longer be accessed.
                </p>
              ) : (
                <>
                  <p className="text-destructive mb-4">{error?.message || 'Failed to load trip'}</p>
                  <Button onClick={() => refetch()} variant="outline">
                    Try Again
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!trip) {
    return (
      <div className="p-8">
        <PageHeader
          title="Trip Not Found"
          subtitle="The trip you're looking for doesn't exist or you don't have access to it"
          actions={
            <Link to="/trips">
              <Button variant="outline" className="flex items-center space-x-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Trips</span>
              </Button>
            </Link>
          }
        />
      </div>
    )
  }

  const tripStatus = getTripStatus(trip)
  const crewCount = trip.members?.length ?? 0
  const tripCoverUrl = (trip as { cover_photo_url?: string | null }).cover_photo_url ?? null

  const hasPendingInvite = !!(trip as { pending_invite?: { id: string } | null }).pending_invite

  const handleAcceptInvite = async () => {
    if (!tripId || !user?.id || isAcceptingInvite) return
    setIsAcceptingInvite(true)
    try {
      const api = new TripsApi(getAccessToken)
      await api.acceptTripInvite(tripId)
      await refetch()
    } catch (err) {
    } finally {
      setIsAcceptingInvite(false)
    }
  }

  return (
    <>
      <PageMeta
        title={trip.name}
        description={trip.description ?? 'View this overlanding trip on SitRepp.io'}
      />
      <div className="@container px-4 py-4 sm:p-6 lg:p-8 overflow-auto relative z-0 min-w-0">
      {/* Admin read-only view banner */}
      {isAdminView && (
        <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
          <p className="text-sm font-medium text-earth-900 dark:text-foreground">
            Viewing as admin (read-only). You are not a member of this trip and cannot make changes.
          </p>
        </div>
      )}
      {trip.retention_window_started_at && canEdit && (
        <div className="mb-6 rounded-lg border border-warning/40 bg-warning/10 dark:bg-warning/15 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-medium text-earth-900 dark:text-foreground">
                This trip is scheduled for permanent deletion
                {retentionPurgeDeadline ? (
                  <>
                    {' '}
                    after{' '}
                    <span className="whitespace-nowrap">{retentionPurgeDeadline.toLocaleDateString()}</span>
                  </>
                ) : null}
                . Restore to extend how long your data is kept.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-warning/50"
            onClick={handleRestoreRetention}
            disabled={isRestoringRetention}
          >
            {isRestoringRetention ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
                Restoring…
              </>
            ) : (
              'Restore trip'
            )}
          </Button>
        </div>
      )}

      {/* Pending invite banner */}
      {hasPendingInvite && (
        <div className="mb-6 rounded-lg border border-adventure-200 dark:border-adventure-800 bg-adventure-50 dark:bg-adventure-950/30 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-adventure-600 dark:text-adventure-400 shrink-0" aria-hidden />
            <p className="text-sm font-medium text-earth-900 dark:text-foreground">
              You&apos;re invited to this trip. Accept to join the crew and get full access.
            </p>
          </div>
          <Button
            onClick={handleAcceptInvite}
            disabled={isAcceptingInvite}
            className="w-full sm:w-auto shrink-0 gap-2"
          >
            {isAcceptingInvite ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <UserPlus className="h-4 w-4" aria-hidden />
            )}
            {isAcceptingInvite ? 'Joining...' : 'Accept invitation'}
          </Button>
        </div>
      )}

      {/* Trip Header */}
      <div className="flex flex-col gap-2 mb-6 min-w-0">

        {/* Row 1: Cover thumbnail + Title + Badge ←→ Action buttons */}
        <div className="flex items-start justify-between gap-4 min-w-0">
          {/* Left: title, badge, optional location */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold text-earth-900 dark:text-foreground truncate min-w-0">{trip.name}</h1>
                <Badge
                  variant={tripStatus === 'active' ? 'default' : 'secondary'}
                  className={cn(
                    tripStatus === 'active' && 'bg-success text-success-foreground',
                    tripStatus === 'planning' && 'bg-adventure-100 text-adventure-800',
                    tripStatus === 'completed' && 'bg-earth-200 text-earth-700'
                  )}
                >
                  {getTripStatusLabel(tripStatus)}
                </Badge>
              </div>
              {trip.meetup_location && (
                <p className="flex items-center gap-1 mt-0.5 text-sm text-earth-500 dark:text-earth-400">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                  {trip.meetup_location}
                </p>
              )}
            </div>
          </div>

          {/* Right: action buttons */}
          {canEdit && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsShareModalOpen(true)}
                className="gap-1.5"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsEditModalOpen(true)} className="gap-1.5">
                <Edit className="h-4 w-4" />
                Edit
              </Button>
              <TripSettingsDropdown
                archivedAt={trip.archived_at ?? null}
                onArchive={handleArchiveTrip}
                onUnarchive={handleUnarchiveTrip}
                onChangeCover={() => setIsCoverDialogOpen(true)}
                onDelete={handleDeleteFromSettings}
              />
            </div>
          )}
        </div>

        {/* Description (if set) */}
        {trip.description && (
          <p className="text-sm text-earth-600 dark:text-muted-foreground max-w-2xl">{trip.description}</p>
        )}

        {/* Row 2: Meta pills */}
        <div className="flex flex-wrap items-center gap-2">
          {daysUntil !== null && daysUntil > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-500/80 px-3 py-1 text-xs font-medium">
              <Clock className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              {daysUntil} days away
            </span>
          )}
          {duration && dateRange && trip.start_date && trip.end_date && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-earth-200/80 dark:border-earth-300 bg-earth-50/80 dark:bg-earth-100 text-earth-600 dark:text-earth-700 px-3 py-1 text-xs font-medium">
              <Calendar className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              {duration} {duration === 1 ? 'day' : 'days'} · {dateRange}
            </span>
          )}
        </div>
      </div>

      {canEdit && tripStatus === 'planning' && !trip.archived_at && (
        <TripNextSteps
          key={trip.id}
          tripId={trip.id}
          trip={trip}
          waypointCount={sortedWaypoints.length}
          memberCount={crewCount}
          canUseRanger={canUseRanger}
          isReady={!isOverviewLoading}
          onStepAction={handleChecklistItemClick}
        />
      )}

      {/* Shared height for section cards (except Route & Itinerary) so grid doesn't shift when items are added */}
      <div className="grid grid-cols-12 gap-x-8 gap-y-10 mb-8">
        {/* Crew x Trip Feed */}
        <div className="col-span-12 lg:col-span-6 min-w-0 h-[380px] flex flex-col min-h-0 overflow-hidden p-1" id="crew-section">
          <CrewSummarySection
            members={trip.members || []}
            crewLimit={trip.crew_limit ?? undefined}
            isOwner={canEdit}
            inviteAction={
              (canEdit || isPrimaryMember) ? (
                <InviteDropdown
                  onInviteWithCode={canEdit ? () => setIsShareModalOpen(true) : undefined}
                  onFindFriends={canEdit ? () => setIsFindFriendsModalOpen(true) : undefined}
                  addPlusOneOptions={(trip?.members ?? [])
                    .filter((m) => !m.parent_member_id)
                    .filter((m) => canEdit || m.user_id === user?.id)
                    .map((m) => ({ id: m.id, name: getMemberDisplayName(m) }))}
                  onAddPlusOne={(parentMemberId, parentMemberName) =>
                    setAddTagAlongParent({ id: parentMemberId, name: parentMemberName })
                  }
                />
              ) : undefined
            }
          />
        </div>
        <div className="col-span-12 lg:col-span-6 min-w-0 h-[380px] flex flex-col min-h-0 overflow-hidden p-1" id="announcements-section">
          <AnnouncementsSection tripId={trip.id} trip={trip} user={user} />
        </div>

        {/* Route & Itinerary — full width (no fixed height) */}
        <section id="route-itinerary-section" className="col-span-12 mt-2">
          <SectionCard
            title="Route & Itinerary"
            count={sortedWaypoints.length}
          >
            {sortedWaypoints.length > 0 ? (
              <div className="rounded-lg overflow-hidden bg-earth-100 dark:bg-earth-900/50 border border-earth-200 dark:border-earth-700 mb-4 min-w-0">
                <div className="h-[280px] sm:h-[360px] md:h-[420px] w-full overflow-hidden">
                  <TripRoute3DPreview
                    tripId={tripId!}
                    fallback={
                      <MapboxMap
                        routes={tripDetailRoutes}
                        waypoints={waypointsWithTripId}
                        selectedWaypoint={selectedWaypoint}
                        onWaypointClick={(wp) => setSelectedDetail({ type: 'waypoint', id: wp.id })}
                        selectedTripId={tripId ?? null}
                        trip={trip}
                        centerOnWaypoints={centerOnWaypoints.length > 0 ? centerOnWaypoints : undefined}
                        tripRouteSchedule={scheduledRoutes}
                        height="100%"
                        className="w-full h-full"
                      />
                    }
                    className="rounded-none"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] rounded-lg border-2 border-dashed border-earth-200 dark:border-earth-700 bg-earth-50 dark:bg-earth-900/30 mb-4">
                <Route className="h-8 w-8 text-earth-300 dark:text-earth-600 mb-2" aria-hidden />
                <p className="text-sm font-medium text-earth-600 dark:text-earth-400">No route planned yet</p>
                <p className="text-xs text-earth-400 dark:text-earth-500 mt-1">Add waypoints below to build your route</p>
              </div>
            )}
            <TripItineraryView
              tripId={trip.id}
              waypoints={sortedWaypoints}
              tripStartDate={trip.start_date ?? null}
              tripEndDate={trip.end_date ?? null}
              isLoading={isOverviewLoading}
              routeSchedule={scheduledRoutes}
              isOwner={canEdit}
              selectedWaypointId={selectedWaypointId}
              onWaypointSelect={(id) => setSelectedDetail({ type: 'waypoint', id })}
              editInPanelMode
              onWaypointAdded={async () => {
                await refresh()
                refetch()
              }}
              onUpdateWaypoint={async (waypointId, updates) => {
                const dto = {
                  ...updates,
                  notes: updates.notes ?? undefined,
                  poi_custom_label: updates.poi_custom_label ?? undefined
                }
                await updateWaypoint(waypointId, dto)
                refresh({ cache: 'no-store' }).catch(() => undefined)
              }}
              onDeleteWaypoint={deleteWaypoint ? async (waypointId) => {
                await deleteWaypoint(waypointId)
                refresh({ cache: 'no-store' }).catch(() => undefined)
                refetch()
              } : undefined}
              onReorderWaypoints={reorderWaypoints ? async (waypointIds) => {
                await reorderWaypoints(waypointIds)
                refresh({ cache: 'no-store' }).catch(() => undefined)
              } : undefined}
            />
          </SectionCard>
        </section>

        {/* Trip Readiness x Weather */}
        <div className="col-span-12 lg:col-span-6 min-w-0 h-[380px] flex flex-col min-h-0 overflow-hidden p-1" id="trip-readiness-section">
          <TripReadinessSection
            tripId={trip.id}
            trip={trip}
            waypointCount={sortedWaypoints.length}
            memberCount={crewCount}
            onChecklistItemClick={canEdit ? handleChecklistItemClick : undefined}
          />
        </div>
        <section className="col-span-12 lg:col-span-6 min-w-0 h-[380px] flex flex-col min-h-0 overflow-hidden p-1">
          <div className="flex-1 min-h-0 overflow-y-auto min-w-0">
            <TripOverviewTab
              tripId={trip.id}
              members={trip.members || []}
              trip={trip}
              isLoading={isLoading}
              onTripUpdate={handleTripUpdate}
              isOwner={canEdit}
              hideRoutePlaceholder
              onInvite={() => {
                document.getElementById('crew-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            />
          </div>
        </section>

        {/* Meals x Gears */}
        <section id="meal-plan-section" className="col-span-12 lg:col-span-6 min-w-0 h-[380px] flex flex-col min-h-0 overflow-hidden p-1">
          <MealPlanSummarySection tripId={trip.id} trip={trip} />
        </section>
        <div className="col-span-12 lg:col-span-6 min-w-0 h-[380px] flex flex-col min-h-0 overflow-hidden p-1" id="gear-section">
          <GearSummarySection tripId={trip.id} />
        </div>

        {/* Photos Gallery */}
        <section id="photos-section" className="col-span-12 mt-2">
          <TripGallerySection
            tripId={trip.id}
            coverPhotoId={(trip as { cover_photo_id?: string | null }).cover_photo_id ?? null}
            canEdit={canEdit}
            onCoverUpdate={async () => {
              refetch()
              await loadTrips({ includeArchived: true }, true)
            }}
          />
        </section>
      </div>

      <TripEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleTripEdit}
        currentTrip={trip as any}
        isLoading={isSaving}
      />
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        tripId={trip.id}
      />
      <FindFriendsModal
        isOpen={isFindFriendsModalOpen}
        onClose={() => setIsFindFriendsModalOpen(false)}
        tripId={trip.id}
        onInviteSent={() => refetch()}
      />
      {addTagAlongParent && (
        <AddTagAlongModal
          isOpen={!!addTagAlongParent}
          onClose={() => setAddTagAlongParent(null)}
          tripId={trip.id}
          parentMemberId={addTagAlongParent.id}
          parentMemberName={addTagAlongParent.name}
          onAdded={handleTripMemberUpdate}
        />
      )}
      <TripCoverDialog
        open={isCoverDialogOpen}
        onOpenChange={setIsCoverDialogOpen}
        tripId={trip.id}
        coverPhotoUrl={tripCoverUrl}
        onCoverUpdate={async () => {
          refetch()
          await loadTrips({ includeArchived: true }, true)
        }}
        getAccessToken={getAccessToken}
      />
      {/* RangerChatButton hidden — crew chat widget occupies bottom-right */}
      </div>
    </>
  )
}

export default function TripDetail() {
  const { tripId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { guestSession } = useGuestSession()
  const isGuestAccess = location.pathname.includes('/guest')

  const authenticatedTrip = useTripData(tripId || '', { includeSubscription: true, enabled: !isGuestAccess })
  const guestTrip = useGuestTrip()
  const dataSource = isGuestAccess ? guestTrip : authenticatedTrip
  const { trip, isLoading, error } = dataSource
  const refetch = 'refetch' in dataSource ? dataSource.refetch : dataSource.loadTrip
  const clearError = () => refetch()

  const memoizedRefetch = useCallback(() => refetch(), [refetch])
  useRealtimeTripMembers({
    tripId: tripId || '',
    onMemberUpdate: memoizedRefetch,
    enabled: !isGuestAccess && !!tripId && !!user
  })

  const guestName = isGuestAccess ? guestTrip.guestName : null
  const handleTripUpdate = useCallback(() => {
    refetch()
  }, [refetch])
  const handleCreateAccount = () => {
    navigate(`/signup?trip=${tripId}&email=${guestSession?.guestEmail || ''}`)
  }

  if (isLoading) {
    return (
      <PageLayout>
        <Container size="app" className="py-8">
          <div className="space-y-6 animate-pulse">
            <div className="space-y-2">
              <div className="h-8 w-64 max-w-md rounded bg-earth-200" />
              <div className="h-4 w-full max-w-xl rounded bg-earth-100" />
              <div className="h-4 w-48 rounded bg-earth-100" />
            </div>
            <div className="flex gap-2 border-b border-earth-200 pb-2">
              <div className="h-10 w-24 rounded bg-earth-200" />
              <div className="h-10 w-24 rounded bg-earth-100" />
              <div className="h-10 w-24 rounded bg-earth-100" />
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="h-48 rounded-lg bg-earth-100" />
              <div className="h-48 rounded-lg bg-earth-100" />
              <div className="h-48 rounded-lg bg-earth-100" />
            </div>
          </div>
        </Container>
      </PageLayout>
    )
  }

  if (error) {
    const isTripDeleted = !isGuestAccess && error instanceof ApiError && (error as ApiError).status === 404
    return (
      <PageLayout>
        <Container size="app" className="py-8">
          <PageHeader
            title={isGuestAccess ? "Guest Access Required" : isTripDeleted ? "Trip No Longer Available" : "Error Loading Trip"}
            subtitle={isGuestAccess ? "Please join the trip to view details" : isTripDeleted ? "This trip has been removed by the owner" : "There was a problem loading the trip details"}
            actions={
              isGuestAccess ? (
                <Link to="/join">
                  <Button className="flex items-center space-x-2">
                    <UserPlus className="w-4 h-4" />
                    <span>How to join</span>
                  </Button>
                </Link>
              ) : (
                <Link to="/dashboard">
                  <Button variant="outline" className="flex items-center space-x-2">
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Dashboard</span>
                  </Button>
                </Link>
              )
            }
          />
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                {isTripDeleted ? (
                  <p className="text-muted-foreground mb-4">
                    This trip is no longer available. The owner has deleted it and it can no longer be accessed.
                  </p>
                ) : isGuestAccess ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      To view this trip as a guest, open the share link your trip organizer sent you.
                    </p>
                    <div className="flex justify-center space-x-2">
                      <Link to="/join">
                        <Button>How to join</Button>
                      </Link>
                      <Button onClick={clearError} variant="outline">
                        Try Again
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-destructive mb-4">{typeof error === 'string' ? error : error?.message ?? 'Something went wrong'}</p>
                    <Button onClick={clearError} variant="outline">
                      Try Again
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </Container>
      </PageLayout>
    )
  }

  if (!trip) {
    return (
      <PageLayout>
        <Container size="app" className="py-8">
          <PageHeader
            title="Trip Not Found"
            subtitle="The trip you're looking for doesn't exist or you don't have access to it"
            actions={
              <Link to="/dashboard">
                <Button variant="outline" className="flex items-center space-x-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Dashboard</span>
                </Button>
              </Link>
            }
          />
        </Container>
      </PageLayout>
    )
  }

  // Authenticated users on guest URL get the new layout instead
  if (!isGuestAccess && location.pathname.includes('/guest') && tripId) {
    navigate(`/trips/${tripId}`, { replace: true })
    return null
  }

  // Render guest view with new components (no nav - guests don't have an account)
  if (isGuestAccess) {
    return (
      <PageLayout showHeader={false}>
        <PageMeta
          title={trip?.name ?? 'Trip'}
          description={trip?.description ?? 'View this overlanding trip on SitRepp.io'}
        />
        <div className="w-full">
          {/* Guest Trip Header - Full Width */}
          <GuestTripHeader
            trip={trip}
            guestName={guestName || 'Guest'}
            onCreateAccount={handleCreateAccount}
          />

          {/* Guest Trip Content */}
          <div className="bg-earth-50 py-8">
            {trip.hasAccess === false ? (
              <Container size="app">
                <GuestAccessDenied
                  trip={trip}
                  tripId={trip.id}
                  onAccessRequested={handleTripUpdate}
                />
              </Container>
            ) : (
              <GuestTripContent
                tripId={trip.id}
                trip={trip}
                members={trip.members || []}
              />
            )}
          </div>

          {/* Guest Trip CTA */}
          {trip.hasAccess !== false && (
            <div className="bg-earth-50 pb-8 px-4 sm:px-6 lg:px-8">
              <Container size="app">
                <GuestTripCTA onCreateAccount={handleCreateAccount} />
              </Container>
            </div>
          )}
        </div>
      </PageLayout>
    )
  }

  // Guest route is guest-only; authenticated users are redirected above
  return null
}
